import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { ref, onValue, get } from "firebase/database";
import { db } from "./firebaseConfig";
import { Elements } from "@stripe/react-stripe-js";
import AppLayout from "./components/AppLayout";
import SizeFilterModal, {
  filterProductsBySize,
} from "./components/SizeFilterModal";
import {
  isValidEmail,
  isValidAddress,
  isValidUSPhone,
  isFormValid as validateForm,
  isBillingValid as validateBilling,
} from "./utils/validationUtils";
import { getImagesArray } from "./utils/imageUtils";
import { getTransformedCloudinaryUrl } from "./utils/cloudinary";
import {
  updateCartItemQuantity,
  removeFromCart,
  calculateTotalCartPrice,
  addToCart,
} from "./utils/cartUtils";
import { ImagePreloader } from "./components/ImagePreloader";
import CartUnavailableModal from "./components/CartUnavailableModal";

const App = ({ stripePromise }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const menuRef = useRef(null);

  const [cartItems, setCartItems] = useState(() => {
    try {
      const stored = localStorage.getItem("cartItems");
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        localStorage.removeItem("cartItems");
        return [];
      }
      return parsed;
    } catch (err) {
      localStorage.removeItem("cartItems");
      return [];
    }
  });

  function handleUpdatePairsToShip(idx, newValue) {
    setCartItems((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, pairsToShip: newValue } : item
      )
    );
  }

  const [clientSecret, setClientSecret] = useState(null);
  const [finalShippingCost, setFinalShippingCost] = useState(0);
  const [finalTaxAmount, setFinalTaxAmount] = useState(0);
  const [finalProcessingFee, setFinalProcessingFee] = useState(0);
  const [finalTotalWithFee, setFinalTotalWithFee] = useState(0);
  const [paymentIntentReady, setPaymentIntentReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [totalCartPrice, setTotalCartPrice] = useState(0);
  const [activePage, setActivePage] = useState("home");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [taxAmount] = useState(0);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    shippingStreet: "",
    shippingLine2: "",
    shippingCity: "",
    shippingState: "",
    shippingPostal: "",
    shippingCountry: "United States",
    billingStreet: "",
    billingLine2: "",
    billingCity: "",
    billingState: "",
    billingPostal: "",
    billingCountry: "United States",
  });
  const [showBilling, setShowBilling] = useState(false);

  const [checkoutStep, setCheckoutStep] = useState(1);
  const checkoutTitles = [
    "Checkout",
    "Contact Information",
    "Shipping Address",
    "Payment",
  ];

  const [stripeReady, setStripeReady] = useState(false);
  const [stripeComplete, setStripeComplete] = useState(false);

  const [services, setServices] = useState([]);

  useEffect(() => {
    const servicesRef = ref(db, "services");
    onValue(servicesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const servicesArray = Object.entries(data).map(([id, service]) => ({
          id,
          ...service,
        }));
        setServices(servicesArray);
      }
    });
  }, []);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setShowCheckoutModal(false);
    setShowProductModal(true);
  };

  const handleAddToCart = (product, quantity = 1) => {
    setCartItems((prevItems) => addToCart(prevItems, product, quantity));
    setShowProductModal(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  // Sync cart across tabs when localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "cartItems" && e.newValue) {
        try {
          const updatedCart = JSON.parse(e.newValue);
          if (Array.isArray(updatedCart)) {
            setCartItems(updatedCart);
          }
        } catch (error) {
          console.error("Error syncing cart from storage:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const [cartUnavailableModalOpen, setCartUnavailableModalOpen] =
    useState(false);

  const handleCheckoutClick = async () => {
    // Wait for products to load before allowing checkout
    if (!productsLoaded) {
      setCheckoutError(
        "Please wait for products to finish loading before checking out."
      );
      return;
    }
    if (cartItems.length > 0) {
      // Build a map of productId -> stock from firebaseProducts
      const stockMap = {};
      firebaseProducts.forEach((product) => {
        stockMap[product.id] = product.stock;
      });
      const availableCart = cartItems.filter((item) => {
        if (services.some((s) => s.id === item.id)) {
          return true;
        }
        const availableStock = stockMap[item.id];
        const requestedQty = item.quantity || 1;
        return availableStock !== undefined && availableStock >= requestedQty;
      });
      const removedCount = cartItems.length - availableCart.length;
      if (removedCount > 0) {
        setCartItems(availableCart);
        // Immediately persist to localStorage for consistency
        localStorage.setItem("cartItems", JSON.stringify(availableCart));
        setCartUnavailableModalOpen(true);
        setShowCheckoutModal(true);
        setCheckoutStep(1);
        setCheckoutError(null);
        return;
      }
    }
    setShowCheckoutModal(true);
    setCheckoutStep(1);
    setCheckoutError(null);
  };

  async function handleNextStep() {
    if (checkoutStep === 3) {
      setLoading(true);
      setCheckoutError(null);
    } else {
      setCheckoutStep((s) => Math.min(s + 1, 4));
    }
  }

  const handlePrevStep = () => setCheckoutStep((s) => Math.max(s - 1, 1));

  // Firebase products
  const [firebaseProducts, setFirebaseProducts] = useState([]);
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    const productsRef = ref(db, "products");

    // Fetch fresh data on page load/refresh to ensure latest data
    const fetchFreshProductsOnLoad = async () => {
      try {
        const snapshot = await get(productsRef);
        const data = snapshot.val();
        if (data) {
          const productsArray = Object.entries(data).map(([id, product]) => ({
            id,
            ...product,
          }));
          setFirebaseProducts(productsArray);
          setProductsLoaded(true);
        } else {
          console.warn("No products data on page load");
          setFirebaseProducts([]);
          setProductsLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching products on load:", error);
        // Try one more time after a delay
        setTimeout(async () => {
          try {
            const snapshot = await get(productsRef);
            const data = snapshot.val();
            if (data) {
              const productsArray = Object.entries(data).map(
                ([id, product]) => ({
                  id,
                  ...product,
                })
              );
              setFirebaseProducts(productsArray);
              setProductsLoaded(true);
              return;
            }
          } catch (retryError) {
            console.error("Retry failed:", retryError);
          }
          setProductsLoaded(true);
        }, 1000);
      }
    };

    // Fetch fresh data on initial page load
    fetchFreshProductsOnLoad();

    // Set up real-time listener for live updates while app is active
    const unsubscribe = onValue(
      productsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const productsArray = Object.entries(data).map(([id, product]) => ({
            id,
            ...product,
          }));
          setFirebaseProducts(productsArray);
        }
      },
      (error) => {
        console.error("Firebase products listener error:", error);
      }
    );

    return unsubscribe;
  }, []);

  // Handle visibility change - force refresh products when user returns to tab
  // This is AFTER firebaseProducts state is defined
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        // User returned to the tab, force a fresh fetch from server
        try {
          const productsRef = ref(db, "products");
          // Call get() to bypass cache and fetch fresh data
          const snapshot = await get(productsRef);
          const data = snapshot.val();
          if (data) {
            const productsArray = Object.entries(data).map(([id, product]) => ({
              id,
              ...product,
            }));
            setFirebaseProducts(productsArray);
            setProductsLoaded(true);
          }
        } catch (error) {
          console.error(
            "Error refreshing products on visibility change:",
            error
          );
          // Don't clear products on error - keep the cached version
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Form validation functions
  const isFormValid = () => validateForm(customerInfo);

  const isBillingValid = () => validateBilling(customerInfo, showBilling);

  const products = useMemo(() => [...firebaseProducts], [firebaseProducts]);

  // Only filter cart items if products have actually loaded AND cart has items
  // This prevents clearing the cart during initial load
  useEffect(() => {
    if (
      productsLoaded &&
      cartItems.length > 0 &&
      (firebaseProducts.length > 0 || services.length > 0)
    ) {
      setCartItems((prevCart) =>
        prevCart.filter(
          (cartItem) =>
            firebaseProducts.some((p) => p.id === cartItem.id) ||
            services.some((s) => s.id === cartItem.id)
        )
      );
    }
  }, [productsLoaded, firebaseProducts, services, cartItems.length]);

  // Use filterProductsBySize from SizeFilterModal
  const filteredProducts = useMemo(() => {
    return filterProductsBySize(products, selectedSizes);
  }, [products, selectedSizes]);

  const handleCloseModal = () => {
    setShowCheckoutModal(false);
    setShowProductModal(false);
    setSelectedProduct(null);
    setCheckoutError(null);
    setCheckoutStep(1);
    setStripeReady(false);
    setStripeComplete(false);
    if (cartItems.length === 0) {
      setClientSecret(null);
    }
  };

  const handleNextImage = () => {
    const imagesArray = getImagesArray(selectedProduct);
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imagesArray.length);
  };

  const handlePrevImage = () => {
    const imagesArray = getImagesArray(selectedProduct);
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + imagesArray.length) % imagesArray.length
    );
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  const storeCustomerInfo = async () => {
    const orderData = {
      ...customerInfo,
      products: cartItems,
    };
    await fetch("/.netlify/functions/store-customer-info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(orderData),
    });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (event.target.closest && event.target.closest(".hamburger-btn")) {
        return;
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setTotalCartPrice(calculateTotalCartPrice(cartItems));
  }, [cartItems]);

  useEffect(() => {
    if (cartItems.length === 0) {
      setClientSecret(null);
    }
  }, [cartItems]);

  // Dynamically build preload URLs based on current page only
  const preloadImageUrls = useMemo(() => {
    const urls = [];

    if (activePage === "home" || activePage === "shop") {
      firebaseProducts.forEach((product) => {
        const imagesArray = getImagesArray(product);
        if (imagesArray[0]) {
          urls.push(
            getTransformedCloudinaryUrl(imagesArray[0], "w_1000,q_auto,f_auto")
          );
        }
      });
    }

    if (activePage === "services") {
      services.forEach((service) => {
        if (service.image) {
          urls.push(
            getTransformedCloudinaryUrl(service.image, "w_1000,q_auto,f_auto")
          );
        }
      });
    }

    // Remove duplicates
    return Array.from(new Set(urls));
  }, [firebaseProducts, services, activePage]);

  useEffect(() => {
    if (showProductModal && selectedProduct) {
      const imagesArray = getImagesArray(selectedProduct);
      imagesArray.forEach((src) => {
        const img = new window.Image();
        img.src = getTransformedCloudinaryUrl(src, "w_1000,q_auto,f_auto");
      });
    }
  }, [showProductModal, selectedProduct]);

  const updateInventory = async (productId, quantitySold) => {
    try {
      const response = await fetch("/.netlify/functions/update-inventory", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          productId: productId,
          quantityToDecrement: quantitySold,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        return { success: true, newStock: data.newStock };
      } else {
        console.error("Failed to update inventory:", data.message);
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
      return { success: false, message: "Network error or unexpected issue." };
    }
  };

  const handlePaymentSuccessAndInventory = async () => {
    setShowPaymentSuccess(false);
    setCartItems([]);
    handleCloseModal();
  };

  const handleStripeReady = useCallback(() => {
    setStripeReady(true);
  }, []);

  const handleStripeChange = useCallback(({ complete }) => {
    setStripeComplete(complete);
  }, []);

  const handleBillingCheckboxChange = (e) => {
    const checked = e.target.checked;
    setShowBilling(!checked);

    if (checked) {
      setCustomerInfo((prev) => ({
        ...prev,
        billingStreet: prev.shippingStreet,
        billingLine2: prev.shippingLine2 || "",
        billingCity: prev.shippingCity,
        billingState: prev.shippingState,
        billingPostal: prev.shippingPostal,
        billingCountry: prev.shippingCountry,
      }));
    }
  };

  const handleRemoveFromCart = (indexToRemove) => {
    const updatedCart = removeFromCart(cartItems, indexToRemove);
    setCartItems(updatedCart);
    if (updatedCart.length === 0) {
      handleCloseModal();
    }
  };

  const handleUpdateCartQuantity = (idx, qty) => {
    setCartItems(updateCartItemQuantity(cartItems, idx, qty));
  };

  if (checkoutStep === 4 && clientSecret) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <AppLayout
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          menuRef={menuRef}
          handleCheckoutClick={handleCheckoutClick}
          cartItems={cartItems}
          setActivePage={setActivePage}
          showProductModal={showProductModal}
          handleCloseModal={handleCloseModal}
          selectedProduct={selectedProduct}
          currentImageIndex={currentImageIndex}
          getImagesArray={getImagesArray}
          handlePrevImage={handlePrevImage}
          handleNextImage={handleNextImage}
          setCurrentImageIndex={setCurrentImageIndex}
          handleAddToCart={handleAddToCart}
          showCheckoutModal={showCheckoutModal}
          checkoutStep={checkoutStep}
          checkoutTitles={checkoutTitles}
          customerInfo={customerInfo}
          showBilling={showBilling}
          setShowBilling={setShowBilling}
          handleCustomerInfoChange={handleCustomerInfoChange}
          handleBillingCheckboxChange={handleBillingCheckboxChange}
          isValidEmail={isValidEmail}
          isValidUSPhone={isValidUSPhone}
          isValidAddress={isValidAddress}
          isFormValid={isFormValid}
          isBillingValid={isBillingValid}
          handlePrevStep={handlePrevStep}
          handleNextStep={handleNextStep}
          loading={loading}
          stripeReady={stripeReady}
          stripeComplete={stripeComplete}
          clientSecret={clientSecret}
          handleStripeReady={handleStripeReady}
          handleStripeChange={handleStripeChange}
          checkoutError={checkoutError}
          setCheckoutError={setCheckoutError}
          updateInventory={updateInventory}
          storeCustomerInfo={storeCustomerInfo}
          setShowPaymentSuccess={setShowPaymentSuccess}
          setCheckoutStep={setCheckoutStep}
          totalCartPrice={totalCartPrice}
          handleRemoveFromCart={handleRemoveFromCart}
          handleProductClick={handleProductClick}
          showPaymentSuccess={showPaymentSuccess}
          handlePaymentSuccessAndInventory={handlePaymentSuccessAndInventory}
          products={filteredProducts}
          services={services}
          activePage={activePage}
          setShowProductModal={setShowProductModal}
          taxAmount={taxAmount}
          finalShippingCost={finalShippingCost}
          finalTaxAmount={finalTaxAmount}
          finalProcessingFee={finalProcessingFee}
          finalTotalWithFee={finalTotalWithFee}
          paymentIntentReady={paymentIntentReady}
          setShowCheckoutModal={setShowCheckoutModal}
          handleUpdateCartQuantity={handleUpdateCartQuantity}
          onShowSizeModal={() => setShowSizeModal(true)}
          selectedSizes={selectedSizes}
          handleUpdatePairsToShip={handleUpdatePairsToShip}
          setClientSecret={setClientSecret}
        />
      </Elements>
    );
  }

  return (
    <>
      <ImagePreloader imageUrls={preloadImageUrls} />
      <AppLayout
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        menuRef={menuRef}
        handleCheckoutClick={handleCheckoutClick}
        cartItems={cartItems}
        setActivePage={setActivePage}
        showProductModal={showProductModal}
        handleCloseModal={handleCloseModal}
        selectedProduct={selectedProduct}
        currentImageIndex={currentImageIndex}
        getImagesArray={getImagesArray}
        handlePrevImage={handlePrevImage}
        handleNextImage={handleNextImage}
        setCurrentImageIndex={setCurrentImageIndex}
        handleAddToCart={handleAddToCart}
        showCheckoutModal={showCheckoutModal}
        checkoutStep={checkoutStep}
        checkoutTitles={checkoutTitles}
        customerInfo={customerInfo}
        showBilling={showBilling}
        setShowBilling={setShowBilling}
        handleCustomerInfoChange={handleCustomerInfoChange}
        handleBillingCheckboxChange={handleBillingCheckboxChange}
        isValidEmail={isValidEmail}
        isValidUSPhone={isValidUSPhone}
        isValidAddress={isValidAddress}
        isFormValid={isFormValid}
        isBillingValid={isBillingValid}
        handlePrevStep={handlePrevStep}
        handleNextStep={handleNextStep}
        loading={loading}
        stripeReady={stripeReady}
        stripeComplete={stripeComplete}
        clientSecret={clientSecret}
        handleStripeReady={handleStripeReady}
        handleStripeChange={handleStripeChange}
        checkoutError={checkoutError}
        setCheckoutError={setCheckoutError}
        updateInventory={updateInventory}
        storeCustomerInfo={storeCustomerInfo}
        setShowPaymentSuccess={setShowPaymentSuccess}
        setCheckoutStep={setCheckoutStep}
        totalCartPrice={totalCartPrice}
        handleRemoveFromCart={handleRemoveFromCart}
        handleProductClick={handleProductClick}
        showPaymentSuccess={showPaymentSuccess}
        handlePaymentSuccessAndInventory={handlePaymentSuccessAndInventory}
        products={filteredProducts}
        services={services}
        activePage={activePage}
        setShowProductModal={setShowProductModal}
        setShowCheckoutModal={setShowCheckoutModal}
        handleUpdateCartQuantity={handleUpdateCartQuantity}
        onShowSizeModal={() => setShowSizeModal(true)}
        selectedSizes={selectedSizes}
        handleUpdatePairsToShip={handleUpdatePairsToShip}
        setClientSecret={setClientSecret}
        finalShippingCost={finalShippingCost}
        setFinalShippingCost={setFinalShippingCost}
        finalTaxAmount={finalTaxAmount}
        setFinalTaxAmount={setFinalTaxAmount}
        finalProcessingFee={finalProcessingFee}
        setFinalProcessingFee={setFinalProcessingFee}
        finalTotalWithFee={finalTotalWithFee}
        setFinalTotalWithFee={setFinalTotalWithFee}
        paymentIntentReady={paymentIntentReady}
        setPaymentIntentReady={setPaymentIntentReady}
      />
      <SizeFilterModal
        show={showSizeModal}
        onClose={() => setShowSizeModal(false)}
        onSelect={setSelectedSizes}
        selected={selectedSizes}
      />
      <CartUnavailableModal
        open={cartUnavailableModalOpen}
        onClose={() => setCartUnavailableModalOpen(false)}
      />
    </>
  );
};

export default App;
